import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem,
  Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import {
  BankFileTemplateService, PaymentRunService,
} from '../../../services/paymentRun.service';
import { BankAccountService } from '../../../services/bankAccount.service';
import { ChequeBookService, ChequeService } from '../../../services/cheque.service';
import { SupplierService } from '../../../services/supplier.service';
import { AuthService } from '../../../services/auth.service';
import {
  BANK_FILE_METHODS, BankChangeFlag, BankFilePreview, BankFileTemplate, CreditInput, OpenCredit, PaymentRun, PaymentRunMethod,
  Proposal, ProposalLine, RunSaveRequest, SupplierCredits,
} from '../../../interfaces/procurement/paymentRun.interface';
import { WithholdingLineInput } from '../../../interfaces/procurement/withholding.interface';
import { BankAccount } from '../../../interfaces/bank/bankAccount.interface';
import { ChequeBook } from '../../../interfaces/bank/cheque.interface';
import { Supplier } from '../../../interfaces/procurement/supplier.interface';
import { useToast } from '../../../context/ToastContext';
import { usePermission } from '../../../hooks/usePermission';
import { PERMISSIONS } from '../../../constants/permissions';
import SearchableSelect from '../../common/SearchableSelect';
import ScrollableTable from '../../common/ScrollableTable';
import { ConfirmDialog } from '../../common/ConfirmDialog';
import { formatMoney, humanizeEnum } from '../../../utils/format';
import { formatDate, toLocalIsoDate } from '../../../utils/dateUtils';
import { saveBlob } from '../../../utils/saveBlob';
import { statusColor } from './PaymentRunList';
import { withholdingMessageSeverity } from '../withholding/warningSeverity';

const METHODS: PaymentRunMethod[] = ['BEFTN', 'RTGS', 'NPSB', 'CHEQUE', 'MANUAL'];

/** A line of a run being drafted. */
interface EditLine {
  billId: string;
  billNumber: string;
  supplierId: string;
  supplierName: string;
  outstanding: number;
  settle: number | '';
  discount: number;
  discountEligible: boolean;
  credits: (CreditInput & { number?: string | null })[];
  withholdings: WithholdingLineInput[];
  withheldEstimate: number;
  warnings: string[];
}

const fromProposal = (l: ProposalLine): EditLine => ({
  billId: l.supplierBillId,
  billNumber: l.billNumber,
  supplierId: l.supplierId,
  supplierName: l.supplierName ?? '',
  outstanding: l.outstanding,
  settle: l.settleAmount,
  discount: l.discountAmount,
  discountEligible: l.discountEligible,
  credits: [],
  withholdings: l.withholdings.map(w => ({ type: w.type, ruleId: w.ruleId, edited: false })),
  withheldEstimate: l.withheldAmount,
  warnings: l.warnings,
});

/** Why a proposal came back empty: names the bills that were left out instead of claiming nothing is due. */
export const emptyProposalMessage = (p: Pick<Proposal, 'blockedBillCount' | 'chequeExcludedCount'>): string => {
  const parts: string[] = [];
  if (p.chequeExcludedCount) parts.push(`${p.chequeExcludedCount} excluded because of issued cheques`);
  if (p.blockedBillCount) parts.push(`${p.blockedBillCount} blocked`);
  return parts.length
    ? `No bills to propose: ${parts.join(', ')}`
    : 'No bills match: nothing is due, or every due bill is already in a run';
};

const cash = (l: EditLine): number =>
  Number(l.settle || 0) - l.credits.reduce((s, c) => s + c.amount, 0) - l.discount;

const KIND_LABEL: Record<string, string> = {
  ADDED: 'New bank account', EDITED: 'Bank details changed', REMOVED: 'Bank account removed',
};

/** B-23: a warning under the bill number when the supplier's bank account changed lately. Never blocks anything. */
export const BankChangeNote: React.FC<{ flag: BankChangeFlag }> = ({ flag }) => {
  const parts: string[] = [];
  if (flag.recentlyChanged && !flag.changedSinceLastPayment) {
    parts.push(`${KIND_LABEL[flag.changeKind] ?? 'Bank details changed'} (last ${flag.windowDays} days)`);
  }
  if (flag.changedSinceLastPayment) parts.push('Changed since the last payment to this supplier');
  if (flag.differsFromLastPayment) parts.push('Different account from the last payment');
  const lines: string[] = [];
  const where = [flag.bankName, flag.maskedAccount].filter(Boolean).join(' ');
  if (where) lines.push(`Pays to ${where}.`);
  if (flag.recentlyChanged && flag.changedAt) {
    lines.push(`Saved by ${flag.changedByName ?? 'an unknown user'} on ${formatDate(flag.changedAt)}.`);
  }
  if (flag.differsFromLastPayment) {
    lines.push(`Last paid to ${[flag.lastPaidBankName, flag.lastPaidMaskedAccount].filter(Boolean).join(' ')}.`);
  }
  return (
    <Box data-help="paymentRun.detail.bankChange" sx={{ mt: 0.5 }}>
      <Tooltip title="The system keeps only who saved the account last and when, not the old account number. Making an account the default or editing its notes also counts as a change. Confirm the account with the supplier by phone before approving.">
        <Chip size="small" color="warning" tabIndex={0} label={parts.join('; ')} sx={{ height: 'auto', '& .MuiChip-label': { whiteSpace: 'normal' } }} />
      </Tooltip>
      <Typography variant="caption" color="text.secondary" display="block">{lines.join(' ')}</Typography>
    </Box>
  );
};

/**
 * One page for the whole life of a payment run (W3-P): new / draft editing, submit, approve, execute, bank file,
 * remittance advice, cheques and reversal. The server owns every rule; this page only shows and sends.
 */
export const PaymentRunDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const institutionId = AuthService.getSelectedInstitution()!!;
  const canManage = usePermission(PERMISSIONS.SUPPLIER_PAYMENT_MANAGE);
  const canApprove = usePermission(PERMISSIONS.SUPPLIER_PAYMENT_APPROVE);

  const [run, setRun] = useState<PaymentRun | null>(null);
  const [loading, setLoading] = useState(!!id);
  const [busy, setBusy] = useState(false);

  // header
  const [banks, setBanks] = useState<BankAccount[]>([]);
  const [books, setBooks] = useState<ChequeBook[]>([]);
  const [templates, setTemplates] = useState<BankFileTemplate[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [bankAccountId, setBankAccountId] = useState('');
  const [method, setMethod] = useState<PaymentRunMethod>('BEFTN');
  const [paymentDate, setPaymentDate] = useState(toLocalIsoDate());
  const [dueOnOrBefore, setDueOnOrBefore] = useState('');
  const [supplierFilter, setSupplierFilter] = useState('');
  const [perSupplierCap, setPerSupplierCap] = useState<number | ''>('');
  const [totalCap, setTotalCap] = useState<number | ''>('');
  const [chequeBookId, setChequeBookId] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [notes, setNotes] = useState('');

  // lines
  const [lines, setLines] = useState<EditLine[]>([]);
  const [creditPool, setCreditPool] = useState<SupplierCredits[]>([]);
  const [proposalWarnings, setProposalWarnings] = useState<string[]>([]);
  const [creditFor, setCreditFor] = useState<EditLine | null>(null);
  const [creditDraft, setCreditDraft] = useState<Record<string, number | ''>>({});

  // dialogs
  const [preview, setPreview] = useState<BankFilePreview | null>(null);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [confirm, setConfirm] = useState<null | 'execute' | 'reverse' | 'cancel' | { line: string }>(null);
  const [comment, setComment] = useState('');

  const isNew = !id;
  const editable = isNew || run?.status === 'DRAFT';

  const applyRun = useCallback((r: PaymentRun) => {
    setRun(r);
    setBankAccountId(r.bankAccountId);
    setMethod(r.method);
    setPaymentDate(r.paymentDate);
    setDueOnOrBefore(r.dueOnOrBefore ?? '');
    setPerSupplierCap(r.perSupplierCap ?? '');
    setTotalCap(r.totalCap ?? '');
    setChequeBookId(r.chequeBookId ?? '');
    setTemplateId(r.bankFileTemplateId ?? '');
    setNotes(r.notes ?? '');
    setLines(r.lines.map(l => ({
      billId: l.supplierBillId,
      billNumber: l.billNumber ?? '',
      supplierId: l.supplierId,
      supplierName: l.supplierName ?? '',
      outstanding: l.outstanding,
      settle: l.settleAmount,
      discount: l.discountAmount,
      discountEligible: l.discountAmount > 0,
      credits: l.credits.filter(c => !c.reversed).map(c => ({
        creditType: c.creditType, creditId: c.creditId, amount: c.amount, number: c.creditNumber,
      })),
      withholdings: l.withholdings.map(w => ({ type: w.type, ruleId: w.ruleId, edited: false })),
      withheldEstimate: l.withheldAmount,
      warnings: l.warnings ?? [],
    })));
  }, []);

  const loadRun = useCallback(async () => {
    if (!id) return;
    try {
      applyRun(await PaymentRunService.get(id));
    } catch (e) {
      toast.showApiError(e, 'Failed to load the payment run');
    } finally {
      setLoading(false);
    }
  }, [id, applyRun]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadRun(); }, [loadRun]);

  useEffect(() => {
    BankAccountService.listByInstitution(institutionId).then(setBanks).catch(() => setBanks([]));
    ChequeBookService.list(institutionId).then(setBooks).catch(() => setBooks([]));
    BankFileTemplateService.list(institutionId).then(setTemplates).catch(() => setTemplates([]));
    Promise.resolve(SupplierService.listByInstitution(0, 500, 'ALL'))
      .then(r => setSuppliers(r.content ?? [])).catch(() => setSuppliers([]));
  }, [institutionId]);

  const bankBooks = useMemo(() => books.filter(b => b.bankAccountId === bankAccountId && b.status !== 'CLOSED'), [books, bankAccountId]);
  const methodTemplates = useMemo(() => templates.filter(t => t.format === method), [templates, method]);

  const propose = async () => {
    if (!bankAccountId) { toast.showError('Choose the bank account to pay from'); return; }
    setBusy(true);
    try {
      const p: Proposal = await PaymentRunService.propose({
        institutionId, bankAccountId, method, paymentDate,
        dueOnOrBefore: dueOnOrBefore || undefined,
        supplierIds: supplierFilter ? [supplierFilter] : undefined,
        perSupplierCap: perSupplierCap === '' ? undefined : Number(perSupplierCap),
        totalCap: totalCap === '' ? undefined : Number(totalCap),
      });
      setLines(p.lines.map(fromProposal));
      setCreditPool(p.credits);
      setProposalWarnings(p.warnings);
      if (p.lines.length === 0) toast.showError(emptyProposalMessage(p));
    } catch (e) {
      toast.showApiError(e, 'Failed to propose bills');
    } finally {
      setBusy(false);
    }
  };

  const updateLine = (billId: string, patch: Partial<EditLine>) =>
    setLines(prev => prev.map(l => (l.billId === billId ? { ...l, ...patch } : l)));

  const saveRequest = (): RunSaveRequest => ({
    institutionId, bankAccountId, method, paymentDate,
    chequeBookId: method === 'CHEQUE' && chequeBookId ? chequeBookId : undefined,
    bankFileTemplateId: BANK_FILE_METHODS.includes(method) && templateId ? templateId : undefined,
    notes: notes.trim() || undefined,
    dueOnOrBefore: dueOnOrBefore || undefined,
    supplierIds: supplierFilter ? [supplierFilter] : undefined,
    perSupplierCap: perSupplierCap === '' ? undefined : Number(perSupplierCap),
    totalCap: totalCap === '' ? undefined : Number(totalCap),
    lines: lines.map(l => ({
      supplierBillId: l.billId,
      settleAmount: Number(l.settle),
      discountAmount: l.discount > 0 ? l.discount : undefined,
      credits: l.credits.length ? l.credits.map(c => ({ creditType: c.creditType, creditId: c.creditId, amount: c.amount })) : undefined,
      withholdings: l.withholdings.length && cash(l) > 0 ? l.withholdings : undefined,
    })),
  });

  const linesInvalid = lines.length === 0 || lines.some(l => l.settle === '' || Number(l.settle) <= 0
    || Number(l.settle) > l.outstanding + 0.004 || cash(l) < -0.004);

  const act = async (fn: () => Promise<PaymentRun>, ok: string, fail: string, stay = true) => {
    setBusy(true);
    try {
      const r = await fn();
      toast.showSuccess(ok);
      if (stay) applyRun(r);
      return r;
    } catch (e) {
      toast.showApiError(e, fail);
      return null;
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    const r = await act(() => (isNew ? PaymentRunService.create(saveRequest()) : PaymentRunService.update(id!, saveRequest())),
      'Draft saved', 'Failed to save the run');
    if (r && isNew) navigate(`/procurement/payment-runs/${r.id}`, { replace: true });
  };

  const submit = async () => {
    // Save first so the server freezes what is on screen, then submit.
    setBusy(true);
    try {
      await PaymentRunService.update(id!, saveRequest());
      applyRun(await PaymentRunService.submit(id!));
      toast.showSuccess('Submitted for approval');
    } catch (e) {
      toast.showApiError(e, 'Failed to submit the run');
    } finally {
      setBusy(false);
    }
  };

  const openCredits = (l: EditLine) => {
    const draft: Record<string, number | ''> = {};
    l.credits.forEach(c => { draft[c.creditId] = c.amount; });
    setCreditDraft(draft);
    setCreditFor(l);
  };

  /** The most that can go on one credit: what it has available, and what the line still has room for. */
  const creditCap = (c: OpenCredit): number => {
    if (!creditFor) return c.available;
    const others = Object.entries(creditDraft)
      .filter(([id]) => id !== c.creditId).reduce((sum, [, v]) => sum + Number(v || 0), 0);
    const room = Math.max(0, Number(creditFor.settle || 0) - creditFor.discount - others);
    return Math.max(0, Math.min(c.available, Math.round(room * 100) / 100));
  };

  const poolFor = (supplierId: string): OpenCredit[] => creditPool.find(c => c.supplierId === supplierId)?.credits ?? [];

  const applyCredits = () => {
    if (!creditFor) return;
    const pool = poolFor(creditFor.supplierId);
    const next: EditLine['credits'] = [];
    pool.forEach(c => {
      const v = creditDraft[c.creditId];
      if (v !== '' && v !== undefined && Number(v) > 0) {
        next.push({ creditType: c.creditType as CreditInput['creditType'], creditId: c.creditId, amount: Number(v), number: c.number });
      }
    });
    // credits already on a saved line that are not in the pool stay as they are
    creditFor.credits.filter(c => !pool.some(p => p.creditId === c.creditId)).forEach(c => next.push(c));
    updateLine(creditFor.billId, { credits: next });
    setCreditFor(null);
  };

  const previewFile = async () => {
    try {
      setPreview(await PaymentRunService.bankFilePreview(id!, templateId || undefined));
    } catch (e) {
      toast.showApiError(e, 'Failed to build the bank file preview');
    }
  };

  const [regenOpen, setRegenOpen] = useState(false);
  // A second download of the same file is how a payment gets sent to the bank twice: ask first, with the count.
  const downloadClicked = () => {
    if (run?.bankFileGeneratedAt) setRegenOpen(true); else downloadFile();
  };

  const downloadFile = async () => {
    setRegenOpen(false);
    try {
      const f = await PaymentRunService.bankFile(id!, templateId || preview?.templateId || undefined);
      saveBlob(f.blob, f.fileName);
      setPreview(null);
      loadRun();
    } catch (e) {
      toast.showApiError(e, 'Failed to download the bank file');
    }
  };

  const openAdvice = async (supplierId: string) => {
    try {
      const blob = await PaymentRunService.advicePdf(id!, supplierId);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.showApiError(e, 'Failed to build the remittance advice');
    }
  };

  const [emailing, setEmailing] = useState<string | null>(null);
  const emailAdvice = async (supplierId: string) => {
    if (emailing) return;
    setEmailing(supplierId);
    try {
      await PaymentRunService.emailAdvice(id!, supplierId);
      toast.showSuccess('Remittance advice e-mailed');
    } catch (e) {
      toast.showApiError(e, 'Failed to e-mail the remittance advice');
    } finally {
      setEmailing(null);
    }
  };

  const printCheque = async (chequeId: string) => {
    try {
      const blob = await ChequeService.print(chequeId);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.showApiError(e, 'Failed to print the cheque');
    }
  };

  const runConfirmed = async () => {
    const c = confirm;
    setConfirm(null);
    if (!c || !id) return;
    if (c === 'execute') await act(() => PaymentRunService.execute(id), 'Run executed: payments posted', 'Failed to execute the run');
    else if (c === 'reverse') await act(() => PaymentRunService.reverse(id), 'Run reversed', 'Failed to reverse the run');
    else if (c === 'cancel') await act(() => PaymentRunService.cancel(id), 'Run cancelled', 'Failed to cancel the run');
    else await act(() => PaymentRunService.reverseLine(c.line), 'Line reversed', 'Failed to reverse the line');
  };

  if (loading) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;

  const totalSettle = lines.reduce((s, l) => s + Number(l.settle || 0), 0);
  const totalCash = lines.reduce((s, l) => s + cash(l), 0);
  const status = run?.status;
  const supplierIdsInRun = Array.from(new Set((run?.lines ?? []).filter(l => !l.reversed).map(l => l.supplierId)));
  const supplierName = (sid: string) => run?.lines.find(l => l.supplierId === sid)?.supplierName ?? sid;

  return (
    <Box data-help-page="paymentRun.detail.page" sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Typography variant="h5">{run ? `Payment Run ${run.runNo}` : 'New Payment Run'}</Typography>
        {status && <Chip color={statusColor(status)} label={humanizeEnum(status)} />}
        <Box sx={{ flexGrow: 1 }} />
        <Button onClick={() => navigate('/procurement/payment-runs')}>Back to list</Button>
      </Box>

      {run?.status === 'REJECTED' && run.rejectReason && <Alert severity="error" sx={{ mb: 2 }}>Rejected: {run.rejectReason}</Alert>}
      {run?.status === 'SUBMITTED' && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Waiting for approval: {run.approvals.filter(a => a.decision === 'APPROVED').length} of {run.approvalsRequired} required.
          The person who made the run cannot approve it.
        </Alert>
      )}
      {run?.status === 'APPROVED' && run.paymentDate > toLocalIsoDate() && (
        <Alert severity="info" sx={{ mb: 2 }}>The payment date is {formatDate(run.paymentDate)}; the run can be executed from that day.</Alert>
      )}
      {proposalWarnings.map(w => <Alert key={w} severity="warning" sx={{ mb: 1 }}>{w}</Alert>)}

      <Paper data-help-section="paymentRun.detail.header" sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          <Box data-help="paymentRun.detail.bank" sx={{ flex: '1 1 260px', maxWidth: 360 }}>
            <SearchableSelect<BankAccount> label="Pay from bank account" size="small" options={banks} disabled={!editable}
              value={bankAccountId} onChange={v => { setBankAccountId(v); setChequeBookId(''); }}
              getOptionLabel={b => `${b.bankName} ${b.accountNumber}`} />
          </Box>
          <TextField data-help="paymentRun.detail.method" select size="small" label="Method" value={method} disabled={!editable} sx={{ flex: '1 1 160px', maxWidth: 200 }}
            onChange={e => { setMethod(e.target.value as PaymentRunMethod); setTemplateId(''); setChequeBookId(''); }}>
            {METHODS.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
          </TextField>
          <TextField data-help="paymentRun.detail.date" size="small" type="date" label="Payment date" value={paymentDate} disabled={!editable}
            onChange={e => setPaymentDate(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ flex: '1 1 170px', maxWidth: 210 }} />
          {method === 'CHEQUE' && (
            <Box data-help="paymentRun.detail.chequeBook" sx={{ flex: '1 1 240px', maxWidth: 320 }}>
              <SearchableSelect<ChequeBook> label="Cheque book" size="small" options={bankBooks} disabled={!editable}
                value={chequeBookId} onChange={setChequeBookId} getOptionLabel={b => `${b.bookLabel} (next ${b.nextLeaf ?? '-'})`} />
            </Box>
          )}
          {BANK_FILE_METHODS.includes(method) && (
            <Box data-help="paymentRun.detail.template" sx={{ flex: '1 1 240px', maxWidth: 320 }}>
              <SearchableSelect<BankFileTemplate> label="Bank-file template" size="small" options={methodTemplates}
                disabled={!!status && status !== 'DRAFT' && status !== 'APPROVED' && status !== 'EXECUTED' && status !== 'SUBMITTED'}
                value={templateId} onChange={setTemplateId} getOptionLabel={t => t.name} />
            </Box>
          )}
        </Box>
        {editable && (
          <>
            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Which bills</Typography>
            <Box data-help-section="paymentRun.detail.propose" sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
              <TextField data-help="paymentRun.detail.dueBefore" size="small" type="date" label="Due on or before" value={dueOnOrBefore}
                onChange={e => setDueOnOrBefore(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ flex: '1 1 170px', maxWidth: 210 }} />
              <Box data-help="paymentRun.detail.supplier" sx={{ flex: '1 1 240px', maxWidth: 320 }}>
                <SearchableSelect<Supplier> label="Supplier (all if empty)" size="small" options={suppliers}
                  value={supplierFilter} onChange={setSupplierFilter} getOptionLabel={s => s.name} />
              </Box>
              <TextField data-help="paymentRun.detail.supplierCap" size="small" type="number" label="Cap per supplier" value={perSupplierCap}
                onChange={e => setPerSupplierCap(e.target.value === '' ? '' : Number(e.target.value))} sx={{ flex: '1 1 150px', maxWidth: 190 }} />
              <TextField data-help="paymentRun.detail.totalCap" size="small" type="number" label="Cap on run total" value={totalCap}
                onChange={e => setTotalCap(e.target.value === '' ? '' : Number(e.target.value))} sx={{ flex: '1 1 150px', maxWidth: 190 }} />
              <Button data-help="paymentRun.detail.action.propose" variant="outlined" onClick={propose} disabled={busy}>Propose bills</Button>
            </Box>
          </>
        )}
        <TextField data-help="paymentRun.detail.notes" size="small" label="Notes" value={notes} disabled={!editable} multiline sx={{ mt: 2, maxWidth: 640 }} fullWidth
          onChange={e => setNotes(e.target.value)} />
      </Paper>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>Bills</Typography>
        {lines.length === 0 ? (
          <Typography color="text.secondary">No bills yet. Choose a bank account and press Propose bills.</Typography>
        ) : (
          <ScrollableTable>
            <Table data-help-section="paymentRun.detail.lines" size="small">
              <TableHead>
                <TableRow>
                  <TableCell data-help="paymentRun.detail.col.bill">Bill</TableCell><TableCell data-help="paymentRun.detail.col.supplier">Supplier</TableCell>
                  <TableCell data-help="paymentRun.detail.col.outstanding" align="right">Outstanding</TableCell>
                  <TableCell data-help="paymentRun.detail.col.settle" align="right">Settle</TableCell>
                  <TableCell data-help="paymentRun.detail.col.credits" align="right">Credits</TableCell>
                  <TableCell data-help="paymentRun.detail.col.discount" align="right">Discount</TableCell>
                  <TableCell data-help="paymentRun.detail.col.cash" align="right">Cash</TableCell>
                  <TableCell data-help="paymentRun.detail.col.withheld" align="right">VDS / TDS</TableCell>
                  <TableCell data-help="paymentRun.detail.col.net" align="right">Net</TableCell>
                  <TableCell data-help="paymentRun.detail.col.actions" align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {editable ? lines.map(l => {
                  const credit = l.credits.reduce((s, c) => s + c.amount, 0);
                  return (
                    <TableRow key={l.billId}>
                      <TableCell>{l.billNumber}{l.warnings.map(w => <Typography key={w} variant="caption" color={withholdingMessageSeverity(w) === 'warning' ? 'warning.main' : 'text.secondary'} display="block">{w}</Typography>)}</TableCell>
                      <TableCell>{l.supplierName}</TableCell>
                      <TableCell align="right">{formatMoney(l.outstanding)}</TableCell>
                      <TableCell align="right">
                        <TextField data-help="paymentRun.detail.action.settle" size="small" type="number" value={l.settle} sx={{ width: 130 }}
                          onChange={e => updateLine(l.billId, { settle: e.target.value === '' ? '' : Number(e.target.value) })}
                          error={l.settle !== '' && Number(l.settle) > l.outstanding + 0.004} />
                      </TableCell>
                      <TableCell align="right">
                        {credit > 0 ? formatMoney(credit) : '—'}
                        {(poolFor(l.supplierId).length > 0 || l.credits.length > 0) && (
                          <Button data-help="paymentRun.detail.action.credits" size="small" onClick={() => openCredits(l)}>Credits</Button>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <TextField data-help="paymentRun.detail.action.discount" size="small" type="number" value={l.discount} sx={{ width: 110 }}
                          onChange={e => updateLine(l.billId, { discount: e.target.value === '' ? 0 : Number(e.target.value) })} />
                      </TableCell>
                      <TableCell align="right">{formatMoney(cash(l))}</TableCell>
                      <TableCell align="right">{l.withholdings.length ? `${formatMoney(l.withheldEstimate)} (estimate, recomputed on save)` : '—'}</TableCell>
                      <TableCell align="right">—</TableCell>
                      <TableCell align="right">
                        <Button data-help="paymentRun.detail.action.removeLine" size="small" color="error" onClick={() => setLines(prev => prev.filter(x => x.billId !== l.billId))}>Remove</Button>
                      </TableCell>
                    </TableRow>
                  );
                }) : (run?.lines ?? []).map(l => (
                  <TableRow key={l.id} sx={l.reversed ? { opacity: 0.55 } : undefined}>
                    <TableCell>
                      {l.billNumber}{l.reversed && <Chip size="small" label="Reversed" sx={{ ml: 1 }} />}
                      {(l.warnings ?? []).map(w => <Typography key={w} data-help="procurement.payment.withholdingWarning" variant="caption" color={withholdingMessageSeverity(w) === 'warning' ? 'warning.main' : 'text.secondary'} display="block">{w}</Typography>)}
                      {l.paymentNumber && <Typography variant="caption" color="text.secondary" display="block">{l.paymentNumber}</Typography>}
                      {l.bankChange && <BankChangeNote flag={l.bankChange} />}
                    </TableCell>
                    <TableCell>{l.supplierName}</TableCell>
                    <TableCell align="right">{formatMoney(l.outstanding)}</TableCell>
                    <TableCell align="right">{formatMoney(l.settleAmount)}</TableCell>
                    <TableCell align="right">{formatMoney(l.creditAmount)}</TableCell>
                    <TableCell align="right">
                      {formatMoney(l.discountAmount)}
                      {l.discountOverride && <Chip size="small" color="warning" label="Override" sx={{ ml: 1 }} />}
                    </TableCell>
                    <TableCell align="right">{formatMoney(l.paymentAmount)}</TableCell>
                    <TableCell align="right">
                      {formatMoney(l.withheldAmount)}
                      {l.withholdings.map(w => w.certificateNumber
                        ? <Typography key={w.type + w.ruleId} variant="caption" display="block">{w.type} cert. {w.certificateNumber}</Typography> : null)}
                    </TableCell>
                    <TableCell align="right">{formatMoney(l.netAmount)}</TableCell>
                    <TableCell align="right">
                      {status === 'EXECUTED' && canManage && !l.reversed && run?.method !== 'CHEQUE' && (
                        <Button data-help="paymentRun.detail.action.reverseLine" size="small" color="warning" onClick={() => setConfirm({ line: l.id })}>Reverse</Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell colSpan={3}><b>Total</b></TableCell>
                  <TableCell align="right"><b>{formatMoney(editable ? totalSettle : run?.totalSettle ?? 0)}</b></TableCell>
                  <TableCell align="right"><b>{!editable && run ? formatMoney(run.totalCredits) : ''}</b></TableCell>
                  <TableCell align="right"><b>{!editable && run ? formatMoney(run.totalDiscount) : ''}</b></TableCell>
                  <TableCell align="right"><b>{editable ? formatMoney(totalCash) : ''}</b></TableCell>
                  <TableCell align="right"><b>{!editable && run ? formatMoney(run.totalWithheld) : ''}</b></TableCell>
                  <TableCell align="right"><b>{!editable && run ? formatMoney(run.totalNet) : ''}</b></TableCell>
                  <TableCell />
                </TableRow>
              </TableBody>
            </Table>
          </ScrollableTable>
        )}
      </Paper>

      {run && run.approvals.length > 0 && (
        <Paper data-help-section="paymentRun.detail.approvals" sx={{ p: 2, mb: 2 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>Approvals</Typography>
          {run.approvals.map(a => (
            <Typography key={a.approverId + a.decidedAt} variant="body2">
              {a.decision === 'APPROVED' ? 'Approved' : 'Rejected'} by {a.approverName ?? a.approverId} on {formatDate(a.decidedAt)}
              {a.comment ? `: ${a.comment}` : ''}
            </Typography>
          ))}
        </Paper>
      )}

      {run && run.method === 'CHEQUE' && run.cheques.length > 0 && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>Cheques</Typography>
          <Table data-help-section="paymentRun.detail.cheques" size="small">
            <TableHead><TableRow><TableCell data-help="paymentRun.detail.cheque.col.number">Cheque no.</TableCell><TableCell data-help="paymentRun.detail.cheque.col.payee">Payee</TableCell><TableCell data-help="paymentRun.detail.cheque.col.amount" align="right">Amount</TableCell>
              <TableCell data-help="paymentRun.detail.cheque.col.status">Status</TableCell><TableCell data-help="paymentRun.detail.cheque.col.actions" align="right">Actions</TableCell></TableRow></TableHead>
            <TableBody>
              {run.cheques.map(c => (
                <TableRow key={c.id}>
                  <TableCell>{c.chequeNumber}</TableCell><TableCell>{c.partyName ?? '—'}</TableCell>
                  <TableCell align="right">{formatMoney(c.amount)}</TableCell><TableCell>{humanizeEnum(c.status)}</TableCell>
                  <TableCell align="right">
                    <Button data-help="paymentRun.detail.cheque.action.print" size="small" onClick={() => printCheque(c.id)}>Print</Button>
                    <Button data-help="paymentRun.detail.cheque.action.register" size="small" onClick={() => navigate('/banking/cheques')}>Cheque register</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Typography variant="caption" color="text.secondary">
            Mark a cheque cleared from the cheque register. Stop or bounce is done here, by reversing the run.
          </Typography>
        </Paper>
      )}

      {status === 'EXECUTED' && supplierIdsInRun.length > 0 && (
        <Paper data-help-section="paymentRun.detail.advice" sx={{ p: 2, mb: 2 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>Remittance advice</Typography>
          {supplierIdsInRun.map(sid => (
            <Box key={sid} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography sx={{ minWidth: 220 }}>{supplierName(sid)}</Typography>
              <Button data-help="paymentRun.detail.advice.pdf" size="small" onClick={() => openAdvice(sid)}>PDF</Button>
              {canManage && <Button data-help="paymentRun.detail.advice.email" size="small" onClick={() => emailAdvice(sid)} disabled={emailing !== null}>{emailing === sid ? 'Sending...' : 'E-mail'}</Button>}
            </Box>
          ))}
        </Paper>
      )}

      <Box data-help-section="paymentRun.detail.actions" sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {editable && canManage && (
          <>
            <Button data-help="paymentRun.detail.action.save" variant="contained" onClick={save} disabled={busy || linesInvalid || !bankAccountId}>Save draft</Button>
            {!isNew && <Button data-help="paymentRun.detail.action.submit" variant="contained" color="success" onClick={submit} disabled={busy || linesInvalid}>Submit for approval</Button>}
          </>
        )}
        {status === 'SUBMITTED' && canApprove && !run?.callerIsMaker && !run?.callerHasApproved && (
          <>
            <TextField data-help="paymentRun.detail.comment" size="small" label="Comment" value={comment} onChange={e => setComment(e.target.value)} sx={{ flex: '1 1 220px', maxWidth: 300 }} />
            <Button data-help="paymentRun.detail.action.approve" variant="contained" color="success" disabled={busy}
              onClick={() => act(() => PaymentRunService.approve(id!, comment.trim() || undefined), 'Approval recorded', 'Failed to approve')}>Approve</Button>
            <Button data-help="paymentRun.detail.action.reject" variant="outlined" color="error" disabled={busy} onClick={() => setRejectOpen(true)}>Reject</Button>
          </>
        )}
        {status === 'SUBMITTED' && canApprove && run?.callerIsMaker && (
          <Typography color="text.secondary" sx={{ alignSelf: 'center' }}>
            You made or submitted this run, so someone else has to approve it.
          </Typography>
        )}
        {status === 'SUBMITTED' && canApprove && !run?.callerIsMaker && run?.callerHasApproved && (
          <Typography color="text.secondary" sx={{ alignSelf: 'center' }}>
            You have already approved this run; a second approval must come from a different approver.
          </Typography>
        )}
        {status === 'REJECTED' && canManage && (
          <Button data-help="paymentRun.detail.action.revise" variant="contained" disabled={busy}
            onClick={() => act(() => PaymentRunService.revise(id!), 'Back to draft', 'Failed to revise the run')}>Revise</Button>
        )}
        {status === 'APPROVED' && canManage && (
          <Button data-help="paymentRun.detail.action.execute" variant="contained" color="success" disabled={busy} onClick={() => setConfirm('execute')}>Execute run</Button>
        )}
        {run && BANK_FILE_METHODS.includes(run.method) && status === 'EXECUTED' && (
          <Button data-help="paymentRun.detail.action.bankFile" variant="outlined" onClick={previewFile} disabled={!templateId}>Bank file</Button>
        )}
        {status === 'EXECUTED' && canManage && <Button data-help="paymentRun.detail.action.reverse" color="warning" variant="outlined" onClick={() => setConfirm('reverse')}>Reverse run</Button>}
        {run && canManage && status && !['EXECUTED', 'REVERSED', 'CANCELLED'].includes(status) && (
          <Button data-help="paymentRun.detail.action.cancel" color="error" onClick={() => setConfirm('cancel')} disabled={busy}>Cancel run</Button>
        )}
      </Box>

      <Dialog data-help-page="procurement.runCreditForm.page" open={!!creditFor} onClose={() => setCreditFor(null)} fullWidth maxWidth="sm">
        <DialogTitle>Apply advances and debit notes to {creditFor?.billNumber}</DialogTitle>
        <DialogContent>
          <Stack spacing={1} sx={{ mt: 1 }}>
            {creditFor && poolFor(creditFor.supplierId).map(c => (
              <Box key={c.creditId} sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Typography sx={{ flex: '1 1 200px' }}>{humanizeEnum(c.creditType)} {c.number} (available {formatMoney(c.available)})</Typography>
                <TextField data-help="procurement.runCreditForm.field.apply" size="small" type="number" label="Apply" value={creditDraft[c.creditId] ?? ''} sx={{ flex: '1 1 120px', maxWidth: 160 }}
                  onChange={e => setCreditDraft(prev => ({ ...prev,
                    [c.creditId]: e.target.value === '' ? '' : Math.min(Math.max(0, Number(e.target.value)), creditCap(c)) }))}
                  helperText={`Up to ${formatMoney(creditCap(c))}: the credit's unapplied amount, capped by what is left to settle on this bill`}
                  inputProps={{ min: 0, max: creditCap(c) }} />
              </Box>
            ))}
            {creditFor && poolFor(creditFor.supplierId).length === 0 && (
              <Typography color="text.secondary">Press Propose bills to load this supplier&apos;s open credits.</Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button data-help="procurement.runCreditForm.cancel" onClick={() => setCreditFor(null)}>Cancel</Button>
          <Button data-help="procurement.runCreditForm.save" variant="contained" onClick={applyCredits}>Apply</Button>
        </DialogActions>
      </Dialog>

      <Dialog data-help-page="procurement.runRejectForm.page" open={rejectOpen} onClose={() => setRejectOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Reject run</DialogTitle>
        <DialogContent>
          <TextField data-help="procurement.runRejectForm.field.reason" fullWidth multiline minRows={2} label="Reason" value={rejectReason} sx={{ mt: 1 }}
            onChange={e => setRejectReason(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button data-help="procurement.runRejectForm.cancel" onClick={() => setRejectOpen(false)}>Cancel</Button>
          <Button data-help="procurement.runRejectForm.save" color="error" variant="contained" disabled={!rejectReason.trim() || busy}
            onClick={async () => {
              const r = await act(() => PaymentRunService.reject(id!, rejectReason.trim()), 'Run rejected', 'Failed to reject');
              if (r) { setRejectOpen(false); setRejectReason(''); }
            }}>Reject</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!preview} onClose={() => setPreview(null)} fullWidth maxWidth="md">
        <DialogTitle>Bank file: {preview?.templateName}</DialogTitle>
        <DialogContent>
          {preview?.warnings.map(w => <Alert key={w} severity="warning" sx={{ mb: 1 }}>{w}</Alert>)}
          <ScrollableTable>
            <Table size="small">
              <TableHead><TableRow><TableCell data-help="paymentRun.detail.preview.col.serial">#</TableCell><TableCell data-help="paymentRun.detail.preview.col.payee">Payee</TableCell><TableCell data-help="paymentRun.detail.preview.col.bank">Bank</TableCell>
                <TableCell data-help="paymentRun.detail.preview.col.routing">Routing</TableCell><TableCell data-help="paymentRun.detail.preview.col.account">Account</TableCell><TableCell data-help="paymentRun.detail.preview.col.amount" align="right">Amount</TableCell></TableRow></TableHead>
              <TableBody>
                {preview?.rows.map(r => (
                  <TableRow key={r.serial}>
                    <TableCell>{r.serial}</TableCell><TableCell>{r.supplierName}</TableCell><TableCell>{r.bankName ?? '—'}</TableCell>
                    <TableCell>{r.routingNumber ?? '—'}</TableCell><TableCell>{r.accountNumber ?? '—'}</TableCell>
                    <TableCell align="right">{formatMoney(r.amount)}</TableCell>
                  </TableRow>
                ))}
                <TableRow><TableCell colSpan={5}><b>Total</b></TableCell><TableCell align="right"><b>{formatMoney(preview?.total ?? 0)}</b></TableCell></TableRow>
              </TableBody>
            </Table>
          </ScrollableTable>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreview(null)}>Close</Button>
          {canManage && <Button data-help="paymentRun.detail.preview.action.download" variant="contained" onClick={downloadClicked} disabled={!preview || preview.rows.length === 0}>Download file</Button>}
        </DialogActions>
      </Dialog>

      <ConfirmDialog open={regenOpen} submitting={false} title="Download the bank file again?"
        message={`A bank file for this run was already made on ${run?.bankFileGeneratedAt ? formatDate(run.bankFileGeneratedAt) : "earlier"}. This file has ${preview?.rows.length ?? 0} payment record(s). Sending it to the bank twice pays the suppliers twice.`}
        confirmLabel="Download again" confirmColor="error"
        onCancel={() => setRegenOpen(false)} onConfirm={downloadFile} />

      <ConfirmDialog open={!!confirm} submitting={busy}
        title={confirm === 'execute' ? 'Execute run' : confirm === 'reverse' ? 'Reverse run' : confirm === 'cancel' ? 'Cancel run' : 'Reverse line'}
        message={confirm === 'execute'
          ? 'This posts every payment, credit application and discount of the run now, all or nothing. It cannot be edited afterwards.'
          : confirm === 'reverse'
            ? 'This reverses every payment, credit application and discount of the run and stops its cheques.'
            : confirm === 'cancel' ? 'The bills in this run become available for other runs.' : 'Reverse this bill\'s payment, credit applications and discount?'}
        confirmLabel={confirm === 'execute' ? 'Execute' : confirm === 'cancel' ? 'Cancel run' : 'Reverse'}
        confirmColor={confirm === 'execute' ? 'primary' : 'error'}
        onCancel={() => setConfirm(null)} onConfirm={runConfirmed} />
    </Box>
  );
};

export default PaymentRunDetail;
